// Network behaviour for every call the registration flow makes.
//
// Two customers reached the selfie step on 2026-09-26, got "Cannot communicate
// with server", and left. Every request that arrived that day was answered 200
// -- 300 of them, none above 2xx -- so nothing reached the server at all. Their
// line dropped, and the page had no answer for that:
//
//   * axios defaults to no timeout, so a request on a dead line hangs forever.
//     The customer sits on a frozen screen with no error and no way back to the
//     button, because nothing ever rejects.
//   * the failures were invisible to us. The error reporter catches them, but
//     it posts over the same broken connection, so the report dies with the
//     request. Of 113 network errors recorded on dev exactly one is against our
//     own API; the rest are third-party scripts failing on healthy lines.
//
// Importing this file configures the axios singleton, so every call in the app
// is covered rather than the handful that were noticed.
import axios from 'axios';

// Long enough that a slow phone on a bad line still finishes, short enough that
// a dead line gives up while the customer is still looking at the screen.
export const DEFAULT_TIMEOUT_MS = 60000;
// Uploads carry photographs. The same line that takes 700ms here on the office
// network takes far longer on a phone in the field.
export const UPLOAD_TIMEOUT_MS = 180000;

const RETRIES = 2;
const RETRY_DELAY_MS = 1500;

axios.defaults.timeout = DEFAULT_TIMEOUT_MS;

/** A failure where the request never got an answer: dropped line, or timeout. */
function isNetworkFailure(error) {
  if (!error || error.response) return false;          // a 4xx/5xx is an answer
  if (axios.isCancel && axios.isCancel(error)) return false;
  return true;
}

/**
 * Retry, but only where retrying cannot create a second record.
 *
 * A GET can be repeated safely. A POST cannot: the client cannot tell "the
 * request never arrived" from "it arrived, was acted on, and the answer was
 * lost on the way back", and retrying the second case registers the customer
 * twice or uploads their documents twice. So a failed POST is reported to the
 * customer and left for them to send again from the button that is already on
 * screen -- their decision, not a duplicate made on their behalf.
 */
function mayRetry(config) {
  return !!config && String(config.method || 'get').toLowerCase() === 'get';
}

axios.interceptors.response.use(null, (error) => {
  const config = error && error.config;
  if (!isNetworkFailure(error) || !mayRetry(config)) return Promise.reject(error);

  config.__nhRetries = (config.__nhRetries || 0) + 1;
  if (config.__nhRetries > RETRIES) return Promise.reject(error);

  return new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * config.__nhRetries))
    .then(() => axios(config));
});

/**
 * What to show when a request never reached us.
 *
 * The old text said only that the server could not be reached, which reads as
 * something the customer cannot do anything about, and they left. It is almost
 * always their connection, and pressing the button again is the fix -- nothing
 * disables it, so it is still there to press.
 */
export const NETWORK_MESSAGE =
  'Cannot communicate with server. Please check your internet connection and try again.';

/** True when this is worth telling the customer to retry, rather than a refusal. */
export function isNetworkError(error) {
  return isNetworkFailure(error);
}
